/**
 * AuthContext.js
 *
 * Authentication context provider for managing user authentication state
 * across the application. Handles login, signup, logout, and token management.
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { SERVER_URL } from './config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          const response = await fetch(`${SERVER_URL}/me`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            console.log('✅ Auth check successful, user:', userData.email);
            setUser(userData);
            setToken(storedToken);
          } else {
            // Token is invalid, clear it
            console.log('❌ Token invalid, clearing...');
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('❌ Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const signup = async (username, email, password) => {
    const response = await fetch(`${SERVER_URL}/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Signup failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);

    // Return full data including Stripe onboarding info
    return {
      ...data,
      stripe_onboarding_required: data.stripe_onboarding_required || false,
      stripe_onboarding_url: data.stripe_onboarding_url || null,
    };
  };

  const login = async (email, password) => {
    const response = await fetch(`${SERVER_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      console.log('❌ No token available for refresh');
      return false;
    }

    try {
      const response = await fetch(`${SERVER_URL}/me`, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return true;
      } else if (response.status === 401) {
        // Token expired or invalid — clear stale auth state
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        return false;
      } else {
        return false;
      }
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
      return false;
    }
  // No reactive deps: reads localStorage directly and SERVER_URL is a module-level constant
  }, []);

  const value = {
    user,
    token,
    loading,
    signup,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
